/**
 * Profile Page.
 *
 * Self-service page where any user can update their name, email,
 * and password without needing an admin.
 */

import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { apiClient } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";

function ProfilePage() {
  const { user } = useAuth();
  const fetchUser = useAuthStore((s) => s.fetchUser);

  // Profile form.
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form.
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await apiClient.put("/api/v1/auth/profile", {
        full_name: fullName,
        email,
      });
      await fetchUser();
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match", "New passwords must match.");
      return;
    }

    setPwLoading(true);
    try {
      const response = await apiClient.put<{ access_token: string }>(
        "/api/v1/auth/password",
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
      );
      // The change signed out every earlier token, including the one that
      // made this request — keep this tab signed in with the fresh one.
      useAuthStore.getState().adoptToken(response.data.access_token);
      toast.success(
        "Password changed",
        "Any other devices have been signed out.",
      );
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      toast.error(
        "Failed to change password",
        typeof detail === "string" ? detail : "Check your current password.",
      );
    } finally {
      setPwLoading(false);
    }
  };

  const roleBadgeVariant =
    user?.role === "owner" || user?.role === "admin"
      ? "danger"
      : user?.role === "manager"
        ? "warning"
        : "default";

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Profile
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Manage your account information.
        </p>
      </div>

      {/* Role & Org Info */}
      <Card>
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--fill-info)] text-lg font-bold text-[var(--color-interactive-primary)]">
            {user?.full_name
              ?.split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)
              .toUpperCase() || "??"}
          </div>
          <div>
            <p className="text-lg font-semibold text-[var(--text-primary)]">
              {user?.full_name}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={roleBadgeVariant}>{user?.role}</Badge>
              <span className="text-sm text-[var(--text-secondary)]">
                {user?.email}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Profile Edit */}
      <Card>
        <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
          Edit Profile
        </h3>
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" isLoading={profileLoading}>
              Save Profile
            </Button>
          </div>
        </form>
      </Card>

      {/* Password Change */}
      <Card>
        <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
          Change Password
        </h3>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" isLoading={pwLoading}>
              Change Password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default ProfilePage;
